import React, { useCallback, useMemo, useState } from 'react';

import { SimpleSlider, TimeSlider } from 'app/home/general-components/Slider/Slider';
import { AmplifierMode } from 'app/model/Device';

import { ReactComponent as IconPlayGray } from 'images/device/audio-controller/play-gray.svg';
import { ReactComponent as IconPlay } from 'images/device/audio-controller/play.svg';
import { ReactComponent as IconPause } from 'images/device/audio-controller/pause.svg';
import { ReactComponent as IconBackwardGray } from 'images/device/audio-controller/backward-gray.svg';
import { ReactComponent as IconBackward } from 'images/device/audio-controller/backward.svg';
import { ReactComponent as IconForwardGray } from 'images/device/audio-controller/forward-gray.svg';
import { ReactComponent as IconForward } from 'images/device/audio-controller/forward.svg';

import { ReactComponent as IconRepeatInactive } from 'images/device/audio-controller/repeat-inactive.svg';
import { ReactComponent as IconRepeatOff } from 'images/device/audio-controller/repeat-off.svg';
import { ReactComponent as IconRepeatOn } from 'images/device/audio-controller/repeat-on.svg';
import { ReactComponent as IconRepeatOne } from 'images/device/audio-controller/repeat-one.svg';
import { ReactComponent as IconShuffleInactive } from 'images/device/audio-controller/shuffle-inactive.svg';
import { ReactComponent as IconShuffleOff } from 'images/device/audio-controller/shuffle-off.svg';
import { ReactComponent as IconShuffle } from 'images/device/audio-controller/shuffle.svg';
import { ReactComponent as IconSpeakerGray } from 'images/device/audio-controller/speaker-gray.svg';
import { ReactComponent as IconSpeaker } from 'images/device/audio-controller/speaker.svg';
import { ReactComponent as IconMute } from 'images/device/audio-controller/mute.svg';

import './AudioControllerPlayer.scss'

export function AudioControllerPlayer({ device }) {
    const on = (device.is_on === true);
    const hinavaMode = (device.mode === AmplifierMode.hinava);
    const withTrackList = (device.track_list !== undefined) && (device.track_list.length > 0);

    const currentTrackDuration = useMemo(() => {
        if (withTrackList) {
            if ((device.track_list_pointer !== undefined) &&
                (device.track_list_pointer >= 0) &&
                (device.track_list_pointer < device.track_list.length)) {
                return device.track_list[device.track_list_pointer]["duration"];
            }
        }
        return 0;
    }, [device, withTrackList])

    return (
        <div className="audio-controller-player">
            <PlayerTop
                passedTime={device.track_passed_time}
                totalTime={currentTrackDuration}
                isActive={on && hinavaMode && withTrackList && (device.track_passed_time >= 0)}
                startTimestamp={device.trackPassedTimeLoadTimestamp}
                isPlaying={device.track_is_playing}
                changeState={device.changeState} />
            <PlayerMiddle
                isPlaying={device.track_is_playing}
                fullMode={on && hinavaMode && withTrackList}
                limitedMode={on && !hinavaMode}
                changeState={device.changeState} />
            <PlayerBottom
                shuffle={device.shuffle}
                repeat={device.repeat}
                isMute={device.is_mute}
                volume={device.volume}
                isActive={on && hinavaMode}
                changeState={device.changeState} />
        </div>
    );
}

function PlayerTop({ startTimestamp, passedTime, totalTime, isActive, changeState, isPlaying }) {
    const setNewPassedTime = useCallback((p) => {
        // console.log(p);
        changeState({ track_passed_time: p });
    }, [changeState]);

    const [passedTimeLabelText, setPassedTimeLabelText] = useState(passedTime);

    if (isActive) {
        return (
            <div className="top">
                <div className="time">{
                    String(formatDigit(Math.floor(passedTimeLabelText / 60))) + ":" +
                    String(formatDigit(passedTimeLabelText % 60))}
                </div>
                <TimeSlider
                    inMotion={isPlaying}
                    startTimestamp={startTimestamp}
                    startPassedTime={passedTime} setNewPassedTime={setNewPassedTime}
                    duration={totalTime}
                    updatePassedTimeForAnotherView={setPassedTimeLabelText} />
                <div className="time">{
                    String(formatDigit(Math.floor(totalTime / 60))) + ":" +
                    String(formatDigit(totalTime % 60))}
                </div>
            </div>
        );
    } else {
        return (
            <div className="top">
                <div className="time inactive">—:—</div>
                <div className="inactive-slider" />
                <div className="time inactive">—:—</div>
            </div>
        );
    }
}

function PlayerMiddle({ isPlaying, fullMode, limitedMode, changeState }) {
    if (fullMode || limitedMode) {
        return (
            <div className="middle">
                <div />
                <IconBackward className="s-icon" onClick={() => { changeState({ function: 1 }) }} />
                {
                    (isPlaying && !limitedMode) ?
                        <IconPause className="b-icon" onClick={() => {
                            if (limitedMode)
                                changeState({ function: 2 })
                            else
                                changeState({ track_is_playing: false })
                        }} /> :
                        <IconPlay className="b-icon" onClick={() => {
                            if (limitedMode)
                                changeState({ function: 2 })
                            else
                                changeState({ track_is_playing: true })
                        }} />
                }
                <IconForward className="s-icon" onClick={() => { changeState({ function: 0 }) }} />
                <div />
            </div>
        );
    } else {
        return (
            <div className="middle">
                <div />
                <IconBackwardGray className="s-icon" />
                <IconPlayGray className="b-icon" />
                <IconForwardGray className="s-icon" />
                <div />
            </div>
        );
    }
}

function PlayerBottom({ shuffle, repeat, isMute, volume, isActive, changeState }) {
    if (isActive) {
        const onMuteClick = () => { changeState({ is_mute: !isMute }) };
        const onShuffleClick = () => { changeState({ shuffle: !shuffle }) };
        const onRepeatClick = () => {
            if (repeat === 0)
                changeState({ repeat: 2 })
            else if (repeat === 2)
                changeState({ repeat: 1 })
            else if (repeat === 1)
                changeState({ repeat: 0 })
        };

        return (
            <div className="bottom">
                <div />
                {shuffle ? <IconShuffle className="icon shuffle" onClick={onShuffleClick} /> :
                    <IconShuffleOff className="icon shuffle" onClick={onShuffleClick} />}
                {isMute ? <IconMute className="icon volume" onClick={onMuteClick} /> :
                    <IconSpeaker className="icon volume" onClick={onMuteClick} />}
                <SimpleSlider
                    value={isMute ? 0 : volume} min={0} max={15}
                    setValue={(volume) => { changeState({ volume, is_mute: false }) }} />
                {(repeat === 0) ?
                    <IconRepeatOff className="icon repeat" onClick={onRepeatClick} /> :
                    ((repeat === 1) ?
                        <IconRepeatOne className="icon repeat" onClick={onRepeatClick} /> :
                        <IconRepeatOn className="icon repeat" onClick={onRepeatClick} />)}
                <div />
            </div>
        );
    } else {
        return (
            <div className="bottom">
                <div />
                <IconShuffleInactive className="icon shuffle" />
                <IconSpeakerGray className="icon volume" />
                <div className="inactive-slider" />
                <IconRepeatInactive className="icon repeat" />
                <div />
            </div>
        );
    }
}

function formatDigit(d) {
    return d < 10 ? ("0" + String(d)) : String(d);
}

export function MiniPlayer({ device }) {
    const on = (device.is_on === true);
    const hinavaMode = (device.mode === AmplifierMode.hinava);
    const withTrackList = (device.track_list !== undefined) && (device.track_list.length > 0);

    return (
        <div className="audio-controller-content mini-player">
            <PlayerMiddle
                isPlaying={device.track_is_playing}
                fullMode={on && hinavaMode && withTrackList}
                limitedMode={on && !hinavaMode}
                changeState={device.changeState} />
            <MiniPlayerVolume
                isMute={device.is_mute}
                volume={device.volume}
                isActive={on && hinavaMode}
                changeState={device.changeState} />
        </div>
    );
}

function MiniPlayerVolume({ isMute, volume, isActive, changeState }) {
    if (isActive) {
        const onMuteClick = () => { changeState({ is_mute: !isMute }) };

        return (
            <div className="bottom">
                {isMute ? <IconMute className="icon volume" onClick={onMuteClick} /> :
                    <IconSpeaker className="icon volume" onClick={onMuteClick} />}
                <SimpleSlider
                    value={isMute ? 0 : volume} min={0} max={15}
                    setValue={(volume) => { changeState({ volume, is_mute: false }) }} />
            </div>
        );
    } else {
        return (
            <div className="bottom">
                <IconSpeakerGray className="icon volume" />
                <div className="inactive-slider" />
            </div>
        );
    }
}